import type { Field, Plan, Activity } from '../types';
import type { AdminUser, AdminEstate } from './cloud';
import { fieldAreaHa } from './domain';

export type ActivityStatusLevel = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'inactive';
export type ActivityActorType = 'customer' | 'superadmin';

export interface AdvisorVisitRecord {
  customerId: string;
  advisorName: string;
  advisorEmail?: string;
  advisorUserId?: string;
  visitedAt: string;
}

export interface UserActivitySummary {
  userId: string;
  name: string;
  email: string;
  deactivated: boolean;
  createdAt?: string; // Oprettelsestidspunkt
  isNew?: boolean; // Oprettet inden for de seneste 7 dage
  estatesCount: number;
  estateNames: string[];
  fieldCount: number;
  totalAreaHa: number;
  lastSignInAt?: string; // Kundens eget login (fra auth.users)
  lastCustomerSignInAt?: string; // Tydelig visning af hvornår kunden selv loggede ind
  lastAdvisorVisitAt?: string; // Hvornår en SuperAdmin sidst var inde på profilen
  lastAdvisorName?: string; // Navnet på SuperAdmin / rådgiveren der besøgte profilen
  latestActivityAt?: string;
  latestActionTitle: string;
  latestActionDetail: string;
  latestActionActor: 'customer' | 'superadmin' | 'none'; // Hvem udførte seneste handling!
  status: ActivityStatusLevel;
  statusBadge: {
    label: string;
    variant: 'success' | 'warning' | 'info' | 'neutral';
    color: string;
    description: string;
  };
  needsFollowUp: boolean;
  followUpReason?: string;
}

export type ActivityFeedType = 'field' | 'plan' | 'care' | 'estate' | 'login' | 'advisor_visit';

export interface ActivityFeedItem {
  id: string;
  type: ActivityFeedType;
  actorType: ActivityActorType; // 'customer' (reel bruger) vs 'superadmin' (rådgiver)
  actorLabel: string; // f.eks. "Kunden selv" eller "SuperAdmin (Rådgiver)"
  userName: string;
  userEmail: string;
  userId: string;
  estateName: string;
  estateId: string;
  timestamp: string;
  title: string;
  detail: string;
  relativeTime: string;
}

export interface ActivityDashboardKPIs {
  activeToday: number;
  activeYesterday: number;
  activeThisWeek: number;
  activeThisMonth: number;
  inactive: number;
  customerLoginsToday: number; // Kunder der selv har været logget ind i dag
  advisorVisitsToday: number; // Kundeprofiler rådgiver har besøgt i dag
  totalCustomers: number;
  totalEstates: number;
  totalFields: number;
  totalAreaHa: number;
  followUpCount: number;
  newCustomersCount: number; // Kunder oprettet inden for de seneste 7 dage
  distribution: {
    todayPct: number;
    thisWeekPct: number;
    thisMonthPct: number;
    inactivePct: number;
  };
}

export interface AdvisorInsight {
  id: string;
  type: 'opportunity' | 'followup' | 'success';
  title: string;
  message: string;
  customerName?: string;
  estateName?: string;
  estateId?: string;
  userId?: string;
}

const ONE_MINUTE = 60 * 1000;
const ONE_HOUR = 60 * ONE_MINUTE;
const ONE_DAY = 24 * ONE_HOUR;

export function getDenmarkDateString(date: Date = new Date()): string {
  return date.toLocaleDateString('en-CA', { timeZone: 'Europe/Copenhagen' });
}

export function getDenmarkYesterdayDateString(now = new Date()): string {
  const cphStr = getDenmarkDateString(now);
  const [y, m, d] = cphStr.split('-').map(Number);
  const prev = new Date(Date.UTC(y, m - 1, d - 1, 12, 0, 0));
  return getDenmarkDateString(prev);
}

export function formatDenmarkTime(date: Date): string {
  return date.toLocaleTimeString('da-DK', {
    timeZone: 'Europe/Copenhagen',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function isTodayInDenmark(dateStringOrMs?: string | number, now = new Date()): boolean {
  if (!dateStringOrMs) return false;
  const d =
    typeof dateStringOrMs === 'number' ? new Date(dateStringOrMs) : new Date(dateStringOrMs);
  if (isNaN(d.getTime())) return false;
  return getDenmarkDateString(d) === getDenmarkDateString(now);
}

export function isYesterdayInDenmark(dateStringOrMs?: string | number, now = new Date()): boolean {
  if (!dateStringOrMs) return false;
  const d =
    typeof dateStringOrMs === 'number' ? new Date(dateStringOrMs) : new Date(dateStringOrMs);
  if (isNaN(d.getTime())) return false;
  return getDenmarkDateString(d) === getDenmarkYesterdayDateString(now);
}

export function isThisWeekInDenmark(dateStringOrMs?: string | number, now = new Date()): boolean {
  if (!dateStringOrMs) return false;
  const d =
    typeof dateStringOrMs === 'number' ? new Date(dateStringOrMs) : new Date(dateStringOrMs);
  if (isNaN(d.getTime())) return false;
  return now.getTime() - d.getTime() <= 7 * 86400000 && d.getTime() <= now.getTime();
}

export function isNewCustomer(createdDateString?: string, days = 7, now = new Date()): boolean {
  if (!createdDateString) return false;
  const d = new Date(createdDateString);
  if (isNaN(d.getTime())) return false;
  return now.getTime() - d.getTime() <= days * 86400000 && d.getTime() <= now.getTime();
}

const ADVISOR_VISITS_KEY = 'plotwise_advisor_visits_v1';
const ADVISOR_ACTIONS_KEY = 'plotwise_advisor_actions_v1';

export function getAdvisorVisits(): Record<string, AdvisorVisitRecord> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(ADVISOR_VISITS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function recordAdvisorVisit(
  customerId: string,
  advisorName = 'Rådgiver Emil Beiner',
  advisorEmail = '',
  advisorUserId = '',
): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const visits = getAdvisorVisits();
    visits[customerId] = {
      customerId,
      advisorName,
      advisorEmail,
      advisorUserId,
      visitedAt: new Date().toISOString(),
    };
    localStorage.setItem(ADVISOR_VISITS_KEY, JSON.stringify(visits));
  } catch {
    // Ignore storage errors
  }
}

export function getAdvisorActions(): Set<string> {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(ADVISOR_ACTIONS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

export function recordAdvisorAction(actionId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const set = getAdvisorActions();
    set.add(actionId);
    localStorage.setItem(ADVISOR_ACTIONS_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // Ignore storage errors
  }
}

export function formatRelativeTimeDanish(isoString?: string, now = new Date()): string {
  if (!isoString) return 'Aldrig';
  const time = Date.parse(isoString);
  if (Number.isNaN(time)) return 'Ugyldigt tidspunkt';

  const diffMs = now.getTime() - time;
  if (diffMs < 0) return 'Lige nu';
  if (diffMs < ONE_MINUTE) return 'Lige nu';

  const diffMinutes = Math.floor(diffMs / ONE_MINUTE);
  if (diffMinutes < 60 && isTodayInDenmark(time, now)) {
    return `For ${diffMinutes} ${diffMinutes === 1 ? 'minut' : 'min.'} siden`;
  }

  const dateObj = new Date(time);
  const timeStr = formatDenmarkTime(dateObj);

  if (isTodayInDenmark(time, now)) {
    return `I dag kl. ${timeStr}`;
  }

  if (isYesterdayInDenmark(time, now)) {
    return `I går kl. ${timeStr}`;
  }

  const diffDays = Math.floor(diffMs / ONE_DAY);
  if (diffDays < 7) {
    return `For ${diffDays} dage siden`;
  }

  const months = [
    'jan.',
    'feb.',
    'mar.',
    'apr.',
    'maj',
    'jun.',
    'jul.',
    'aug.',
    'sep.',
    'okt.',
    'nov.',
    'dec.',
  ];
  return `${dateObj.getDate()}. ${months[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
}

export function formatExactTimeDanish(isoString?: string): string {
  if (!isoString) return '–';
  const time = Date.parse(isoString);
  if (Number.isNaN(time)) return '–';
  const d = new Date(time);
  const pad = (n: number) => String(n).padStart(2, '0');
  const months = [
    'jan.',
    'feb.',
    'mar.',
    'apr.',
    'maj',
    'jun.',
    'jul.',
    'aug.',
    'sep.',
    'okt.',
    'nov.',
    'dec.',
  ];
  return `${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()} kl. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function determineActivityStatus(
  latestTimeMs: number,
  nowMs: number,
): {
  status: ActivityStatusLevel;
  badge: {
    label: string;
    variant: 'success' | 'warning' | 'info' | 'neutral';
    color: string;
    description: string;
  };
} {
  if (!latestTimeMs || Number.isNaN(latestTimeMs)) {
    return {
      status: 'inactive',
      badge: {
        label: 'Aldrig logget ind',
        variant: 'neutral',
        color: '#52525b',
        description: 'Brugeren har ikke været logget ind endnu.',
      },
    };
  }

  const now = new Date(nowMs);
  if (isTodayInDenmark(latestTimeMs, now)) {
    return {
      status: 'today',
      badge: {
        label: 'Aktiv i dag',
        variant: 'success',
        color: '#15803d',
        description: 'Brugeren har været aktiv i dag.',
      },
    };
  }

  if (isYesterdayInDenmark(latestTimeMs, now)) {
    return {
      status: 'yesterday',
      badge: {
        label: 'Aktiv i går',
        variant: 'warning',
        color: '#b45309',
        description: 'Brugeren var senest aktiv i går.',
      },
    };
  }

  if (isThisWeekInDenmark(latestTimeMs, now)) {
    return {
      status: 'this_week',
      badge: {
        label: 'Aktiv denne uge',
        variant: 'warning',
        color: '#a16207',
        description: 'Brugeren har været aktiv inden for de seneste 7 dage.',
      },
    };
  }

  const diffMs = nowMs - latestTimeMs;
  if (diffMs <= 30 * ONE_DAY) {
    return {
      status: 'this_month',
      badge: {
        label: 'Aktiv denne måned',
        variant: 'info',
        color: '#2563eb',
        description: 'Brugeren har været aktiv inden for de seneste 30 dage.',
      },
    };
  }

  return {
    status: 'inactive',
    badge: {
      label: 'Inaktiv (+30 dage)',
      variant: 'neutral',
      color: '#52525b',
      description: 'Over 30 dage siden seneste login eller aktivitet.',
    },
  };
}

export function buildUserActivitySummaries(
  users: AdminUser[],
  estates: AdminEstate[],
  fields: Field[],
  plans: Plan[],
  activities: Activity[],
  now = new Date(),
  customAdvisorVisits?: Record<string, AdvisorVisitRecord>,
  customAdvisorActions?: Set<string>,
): UserActivitySummary[] {
  const nowMs = now.getTime();
  const advisorVisits = customAdvisorVisits ?? getAdvisorVisits();
  const advisorActions = customAdvisorActions ?? getAdvisorActions();

  return users.map((user) => {
    const userEstates = estates.filter(
      (e) =>
        e.owner_id === user.user_id ||
        (Array.isArray(e.members) && e.members.some((m) => m.userId === user.user_id)),
    );
    const estateIds = new Set(userEstates.map((e) => e.estate_id));
    const userFields = fields.filter((f) => estateIds.has(f.estateId));
    const userPlans = plans.filter((p) => estateIds.has(p.estateId));
    const userActivities = activities.filter((a) => estateIds.has(a.estateId));

    const totalAreaHa = userFields.reduce((sum, f) => sum + fieldAreaHa(f.geometry), 0);

    // Kundens eget login tidspunkt (Reel bruger)
    const customerLoginTimeMs = user.last_sign_in_at ? Date.parse(user.last_sign_in_at) : 0;

    // Rådgiverbesøg (SuperAdmin der har været inde på profilen)
    const advisorVisit = advisorVisits[user.user_id];
    const advisorVisitTimeMs = advisorVisit ? Date.parse(advisorVisit.visitedAt) : 0;

    // Find den seneste handling blandt kundens data
    let latestTimeMs = customerLoginTimeMs;
    let latestActionTitle = user.last_sign_in_at
      ? 'Logget ind i Plotwise'
      : 'Ingen handlinger endnu';
    let latestActionDetail = user.last_sign_in_at ? 'Kunden selv loggede ind' : 'Konto oprettet';
    let latestActionActor: 'customer' | 'superadmin' | 'none' = user.last_sign_in_at
      ? 'customer'
      : 'none';

    // Hvis rådgiver har besøgt profilen for nylig, tjek om det er senest
    if (advisorVisitTimeMs > latestTimeMs) {
      latestTimeMs = advisorVisitTimeMs;
      latestActionTitle = `Besøgt af ${advisorVisit.advisorName || 'SuperAdmin'}`;
      latestActionDetail = 'Rådgiver gennemgik kundens arbejdsplads';
      latestActionActor = 'superadmin';
    }

    // Tjek revirer
    for (const estate of userEstates) {
      if (estate.created_at) {
        const t = Date.parse(estate.created_at);
        if (t > latestTimeMs) {
          latestTimeMs = t;
          latestActionTitle = `Oprettede revir "${estate.name}"`;
          latestActionDetail = estate.address || 'Revir tilføjet';
          latestActionActor = advisorActions.has(`estate-${estate.estate_id}`)
            ? 'superadmin'
            : 'customer';
        }
      }
    }

    // Tjek marker
    for (const field of userFields) {
      if (field.updatedAt) {
        const t = Date.parse(field.updatedAt);
        if (t > latestTimeMs) {
          latestTimeMs = t;
          const ha = fieldAreaHa(field.geometry);
          const isAdvisor = advisorActions.has(`field-${field.id}`);
          latestActionTitle = `Tegnede/redigerede mark "${field.name}"`;
          latestActionDetail = `${ha.toFixed(2)} ha vildtager (${isAdvisor ? 'af rådgiver' : 'af kunden selv'})`;
          latestActionActor = isAdvisor ? 'superadmin' : 'customer';
        }
      }
    }

    // Tjek planer
    for (const plan of userPlans) {
      if (plan.updatedAt) {
        const t = Date.parse(plan.updatedAt);
        if (t > latestTimeMs) {
          latestTimeMs = t;
          const blendCount = Object.keys(plan.selections || {}).length;
          const isAdvisor = advisorActions.has(`plan-${plan.id}`);
          latestActionTitle = `Opdaterede biotopplan "${plan.name}"`;
          latestActionDetail = `${blendCount} ${blendCount === 1 ? 'frøblanding' : 'frøblandinger'} valgt (${isAdvisor ? 'af rådgiver' : 'af kunden'})`;
          latestActionActor = isAdvisor ? 'superadmin' : 'customer';
        }
      }
    }

    // Tjek aktiviteter
    for (const act of userActivities) {
      const actDate = act.createdAt || act.date;
      if (actDate) {
        const t = Date.parse(actDate);
        if (t > latestTimeMs) {
          latestTimeMs = t;
          const isAdvisor = advisorActions.has(`care-${act.id}`);
          latestActionTitle = `${act.kind} (${act.status === 'done' ? 'Udført' : 'Planlagt'})`;
          latestActionDetail = act.description || `Plejeaktivitet: ${act.kind}`;
          latestActionActor = isAdvisor ? 'superadmin' : 'customer';
        }
      }
    }

    const { status, badge } = determineActivityStatus(latestTimeMs, nowMs);

    // Opfølgnings-vurdering
    let needsFollowUp = false;
    let followUpReason = '';

    if (userEstates.length === 0 && !user.deactivated) {
      needsFollowUp = true;
      followUpReason = 'Mangler at få oprettet et revir.';
    } else if (userEstates.length > 0 && userFields.length === 0 && !user.deactivated) {
      needsFollowUp = true;
      followUpReason = 'Har oprettet revir, men mangler at tegne sine vildtagre.';
    } else if (customerLoginTimeMs === 0 && !user.deactivated) {
      needsFollowUp = true;
      followUpReason = 'Kunden har aldrig selv været logget ind. Send velkomstmail/opkald.';
    } else if (status === 'inactive' && !user.deactivated) {
      needsFollowUp = true;
      followUpReason = 'Ikke aktiv i over 30 dage. Godt tidspunkt at følge op på biotopplanen.';
    }

    return {
      userId: user.user_id,
      name: user.name || user.email.split('@')[0],
      email: user.email,
      deactivated: Boolean(user.deactivated),
      createdAt: user.created_at || undefined,
      isNew: isNewCustomer(user.created_at, 7, now),
      estatesCount: userEstates.length,
      estateNames: userEstates.map((e) => e.name),
      fieldCount: userFields.length,
      totalAreaHa: Math.round(totalAreaHa * 100) / 100,
      lastSignInAt: user.last_sign_in_at || undefined,
      lastCustomerSignInAt: user.last_sign_in_at || undefined,
      lastAdvisorVisitAt: advisorVisit?.visitedAt || undefined,
      lastAdvisorName: advisorVisit?.advisorName || undefined,
      latestActivityAt: latestTimeMs ? new Date(latestTimeMs).toISOString() : undefined,
      latestActionTitle,
      latestActionDetail,
      latestActionActor,
      status,
      statusBadge: badge,
      needsFollowUp,
      followUpReason: followUpReason || undefined,
    };
  });
}

export function buildActivityFeed(
  users: AdminUser[],
  estates: AdminEstate[],
  fields: Field[],
  plans: Plan[],
  activities: Activity[],
  now = new Date(),
  customAdvisorVisits?: Record<string, AdvisorVisitRecord>,
  customAdvisorActions?: Set<string>,
): ActivityFeedItem[] {
  const feed: ActivityFeedItem[] = [];
  const advisorVisits = customAdvisorVisits ?? getAdvisorVisits();
  const advisorActions = customAdvisorActions ?? getAdvisorActions();

  const estateMap = new Map(estates.map((e) => [e.estate_id, e]));
  const userMap = new Map(users.map((u) => [u.user_id, u]));

  const getUserName = (userId: string, email = ''): string => {
    const u = userMap.get(userId);
    if (u?.name) return u.name;
    if (email) return email.split('@')[0];
    return 'Bruger';
  };

  // 1. Rådgiverbesøg (SuperAdmin i kundens workspace)
  for (const [customerId, visit] of Object.entries(advisorVisits)) {
    const customer = userMap.get(customerId);
    feed.push({
      id: `advisor-visit-${customerId}-${visit.visitedAt}`,
      type: 'advisor_visit',
      actorType: 'superadmin',
      actorLabel: `SuperAdmin (${visit.advisorName || 'Rådgiver'})`,
      userName: customer?.name || customer?.email || 'Kunde',
      userEmail: customer?.email || '',
      userId: customerId,
      estateName: '',
      estateId: '',
      timestamp: visit.visitedAt,
      title: 'Rådgiverbesøg på kundens profil',
      detail: `${visit.advisorName || 'Rådgiver Emil Beiner'} åbnede og gennemgik kundens revirer`,
      relativeTime: formatRelativeTimeDanish(visit.visitedAt, now),
    });
  }

  // 2. Kundens eget login (Reel bruger)
  for (const user of users) {
    if (user.last_sign_in_at) {
      feed.push({
        id: `login-${user.user_id}-${user.last_sign_in_at}`,
        type: 'login',
        actorType: 'customer',
        actorLabel: 'Kunden selv (Reel bruger)',
        userName: user.name || user.email.split('@')[0],
        userEmail: user.email,
        userId: user.user_id,
        estateName: '',
        estateId: '',
        timestamp: user.last_sign_in_at,
        title: 'Kunden loggede ind i Plotwise',
        detail: 'Reel kundesession startet med e-mail og adgangskode',
        relativeTime: formatRelativeTimeDanish(user.last_sign_in_at, now),
      });
    }
  }

  // 3. Revirer oprettet
  for (const estate of estates) {
    if (estate.created_at) {
      const owner =
        (Array.isArray(estate.members) ? estate.members : []).find((m) => m.role === 'owner') ||
        estate.members?.[0];
      const isAdvisor = advisorActions.has(`estate-${estate.estate_id}`);
      feed.push({
        id: `estate-${estate.estate_id}`,
        type: 'estate',
        actorType: isAdvisor ? 'superadmin' : 'customer',
        actorLabel: isAdvisor ? 'SuperAdmin / Rådgiver' : 'Kunden selv',
        userName: owner
          ? getUserName(owner.userId, owner.email)
          : estate.owner_name || 'Administrator',
        userEmail: owner?.email || estate.owner_email || '',
        userId: owner?.userId || estate.owner_id || '',
        estateName: estate.name,
        estateId: estate.estate_id,
        timestamp: estate.created_at,
        title: `Nyt revir oprettet: "${estate.name}"`,
        detail: estate.address || 'Revir tilføjet til porteføljen',
        relativeTime: formatRelativeTimeDanish(estate.created_at, now),
      });
    }
  }

  // 4. Marker optegnet / redigeret
  for (const field of fields) {
    if (field.updatedAt) {
      const estate = estateMap.get(field.estateId);
      const owner =
        (Array.isArray(estate?.members) ? estate.members : []).find((m) => m.role === 'owner') ||
        estate?.members?.[0];
      const ha = fieldAreaHa(field.geometry);
      const isAdvisor = advisorActions.has(`field-${field.id}`);
      feed.push({
        id: `field-${field.id}-${field.updatedAt}`,
        type: 'field',
        actorType: isAdvisor ? 'superadmin' : 'customer',
        actorLabel: isAdvisor ? 'SuperAdmin / Rådgiver' : 'Kunden selv',
        userName: owner ? getUserName(owner.userId, owner.email) : estate?.owner_name || 'Rådgiver',
        userEmail: owner?.email || estate?.owner_email || '',
        userId: owner?.userId || estate?.owner_id || '',
        estateName: estate?.name || 'Revir',
        estateId: field.estateId,
        timestamp: field.updatedAt,
        title: `Vildtager optegnet: "${field.name}"`,
        detail: `${ha.toFixed(2)} ha optegnet på ${estate?.name || 'reviret'}`,
        relativeTime: formatRelativeTimeDanish(field.updatedAt, now),
      });
    }
  }

  // 5. Planer opdateret
  for (const plan of plans) {
    if (plan.updatedAt) {
      const estate = estateMap.get(plan.estateId);
      const owner =
        (Array.isArray(estate?.members) ? estate.members : []).find((m) => m.role === 'owner') ||
        estate?.members?.[0];
      const blendCount = Object.keys(plan.selections || {}).length;
      const isAdvisor = advisorActions.has(`plan-${plan.id}`);
      feed.push({
        id: `plan-${plan.id}-${plan.updatedAt}`,
        type: 'plan',
        actorType: isAdvisor ? 'superadmin' : 'customer',
        actorLabel: isAdvisor ? 'SuperAdmin / Rådgiver' : 'Kunden selv',
        userName: owner ? getUserName(owner.userId, owner.email) : estate?.owner_name || 'Rådgiver',
        userEmail: owner?.email || estate?.owner_email || '',
        userId: owner?.userId || estate?.owner_id || '',
        estateName: estate?.name || 'Revir',
        estateId: plan.estateId,
        timestamp: plan.updatedAt,
        title: `Biotopplan opdateret: "${plan.name}"`,
        detail: `${blendCount} ${blendCount === 1 ? 'frøblanding valgt' : 'frøblandinger valgt'} (${plan.year})`,
        relativeTime: formatRelativeTimeDanish(plan.updatedAt, now),
      });
    }
  }

  // 6. Plejeaktiviteter
  for (const act of activities) {
    const actDate = act.createdAt || act.date;
    if (actDate) {
      const estate = estateMap.get(act.estateId);
      const owner =
        (Array.isArray(estate?.members) ? estate.members : []).find((m) => m.role === 'owner') ||
        estate?.members?.[0];
      const isAdvisor = advisorActions.has(`care-${act.id}`);
      feed.push({
        id: `care-${act.id}-${actDate}`,
        type: 'care',
        actorType: isAdvisor ? 'superadmin' : 'customer',
        actorLabel: isAdvisor ? 'SuperAdmin / Rådgiver' : 'Kunden selv',
        userName: owner ? getUserName(owner.userId, owner.email) : 'Rådgiver',
        userEmail: owner?.email || '',
        userId: owner?.userId || '',
        estateName: estate?.name || 'Revir',
        estateId: act.estateId,
        timestamp: actDate,
        title: `Plejeaktivitet: ${act.kind} (${act.status === 'done' ? 'Gennemført' : 'Planlagt'})`,
        detail: act.description || `${act.kind} på ${estate?.name || 'reviret'}`,
        relativeTime: formatRelativeTimeDanish(actDate, now),
      });
    }
  }

  // Sort descending: newest first
  return feed.sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp));
}

export function calculateActivityKPIs(
  summaries: UserActivitySummary[],
  estates: AdminEstate[],
  now = new Date(),
): ActivityDashboardKPIs {
  const activeCustomers = summaries.filter((s) => !s.deactivated);
  const total = activeCustomers.length || 1;

  const todayCount = activeCustomers.filter((s) => s.status === 'today').length;
  const yesterdayCount = activeCustomers.filter((s) => s.status === 'yesterday').length;
  const thisWeekCount = activeCustomers.filter(
    (s) => s.status === 'this_week' || s.status === 'yesterday',
  ).length;
  const thisMonthCount = activeCustomers.filter((s) => s.status === 'this_month').length;
  const inactiveCount = activeCustomers.filter((s) => s.status === 'inactive').length;

  const customerLoginsToday = activeCustomers.filter((s) => {
    if (!s.lastCustomerSignInAt) return false;
    return isTodayInDenmark(s.lastCustomerSignInAt, now);
  }).length;

  const advisorVisitsToday = activeCustomers.filter((s) => {
    if (!s.lastAdvisorVisitAt) return false;
    return isTodayInDenmark(s.lastAdvisorVisitAt, now);
  }).length;

  const newCustomersCount = summaries.filter((s) => isNewCustomer(s.createdAt, 7, now)).length;

  const totalFields = summaries.reduce((acc, s) => acc + s.fieldCount, 0);
  const totalAreaHa = summaries.reduce((acc, s) => acc + s.totalAreaHa, 0);
  const followUpCount = activeCustomers.filter((s) => s.needsFollowUp).length;

  return {
    activeToday: todayCount,
    activeYesterday: yesterdayCount,
    activeThisWeek: thisWeekCount,
    activeThisMonth: thisMonthCount,
    inactive: inactiveCount,
    customerLoginsToday,
    advisorVisitsToday,
    totalCustomers: activeCustomers.length,
    totalEstates: estates.filter((e) => e.active).length,
    totalFields,
    totalAreaHa: Math.round(totalAreaHa * 10) / 10,
    followUpCount,
    newCustomersCount,
    distribution: {
      todayPct: Math.round((todayCount / total) * 100),
      thisWeekPct: Math.round((thisWeekCount / total) * 100),
      thisMonthPct: Math.round((thisMonthCount / total) * 100),
      inactivePct: Math.round((inactiveCount / total) * 100),
    },
  };
}

export function generateAdvisorInsights(
  summaries: UserActivitySummary[],
  estates: AdminEstate[],
): AdvisorInsight[] {
  const insights: AdvisorInsight[] = [];

  // 1. Kunder der aldrig selv har logget ind
  const neverSignedIn = summaries.filter(
    (s) => !s.deactivated && s.estatesCount > 0 && !s.lastCustomerSignInAt,
  );
  if (neverSignedIn.length > 0) {
    const first = neverSignedIn[0];
    insights.push({
      id: `never-signed-in-${first.userId}`,
      type: 'followup',
      title: 'Kunde har aldrig selv logget ind',
      message: `${first.name} har endnu ikke logget ind på sin profil. Rådgiver kan have sat reviret op, men kunden mangler selv at tage systemet i brug.`,
      customerName: first.name,
      userId: first.userId,
    });
  }

  // 2. Kunder der selv har været logget ind i dag
  const selfActiveToday = summaries.filter(
    (s) => !s.deactivated && s.latestActionActor === 'customer' && s.status === 'today',
  );
  if (selfActiveToday.length > 0) {
    const names = selfActiveToday
      .slice(0, 3)
      .map((s) => s.name)
      .join(', ');
    insights.push({
      id: 'self-active-today',
      type: 'success',
      title: 'Kunder selvstændigt aktive i dag',
      message: `${selfActiveToday.length} ${selfActiveToday.length === 1 ? 'kunde har' : 'kunder har'} selv logget ind og arbejdet i dag (${names}). Godt engagement!`,
    });
  }

  // 3. Kunder med rådgiverbesøg som afventer kundens godkendelse
  const advisorVisitedRecently = summaries.filter(
    (s) => !s.deactivated && s.lastAdvisorVisitAt && !s.lastCustomerSignInAt,
  );
  if (advisorVisitedRecently.length > 0) {
    const first = advisorVisitedRecently[0];
    insights.push({
      id: `advisor-visited-${first.userId}`,
      type: 'opportunity',
      title: 'Revir optegnet af rådgiver – afventer kunden',
      message: `Rådgiver ${first.lastAdvisorName || 'Emil'} har været inde og klargøre revir for ${first.name}. Giv kunden besked om at logge ind og se resultatet.`,
      customerName: first.name,
      userId: first.userId,
    });
  }

  return insights;
}
